"use client";

import React, { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ProductsSearch, useWPQueryForSearch, buildProductSearchUrl } from "@/components/shop/ProductsSearch";
import { CartIcon } from "@/components/shop/CartIcon";
import { useWooAccountUrl } from "@/integrations/wordpress/useWooAccountUrl";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Menu, Search, User, X } from "lucide-react";

// Recursive dropdown renderer for desktop navigation
function DesktopSubmenu({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className="flex flex-col gap-1 min-w-[190px] p-1">
      {items.map((subItem, index) => <li key={subItem.id} data-index={index} className="w-full">
          {subItem.children && subItem.children.length > 0 ? <div className="space-y-1">
              <Link to={subItem.href || "#"} className="block px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                {subItem.label}
              </Link>
              <div className="pl-2 border-l border-border">
                <DesktopSubmenu items={subItem.children} depth={depth + 1} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={subItem.href || "#"} className="block px-3 py-1.5 text-sm text-foreground hover:text-primary transition-colors">
                {subItem.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}

// Consumer for desktop navigation
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex items-center gap-6">
        <div className="h-4 w-14 bg-muted animate-pulse rounded-xs" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-xs" />
        <div className="h-4 w-12 bg-muted animate-pulse rounded-xs" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-xs" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-5">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent focus:bg-transparent text-sm font-normal tracking-wide text-foreground hover:text-primary transition-colors px-1 py-1 h-auto data-[state=open]:bg-transparent">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="border border-border bg-popover text-popover-foreground shadow-sm">
                  <DesktopSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="text-sm font-normal tracking-wide text-foreground hover:text-primary transition-colors px-1 py-1">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

// Recursive list renderer for mobile drawer menu
function MobileMenuTree({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="flex flex-col space-y-3">
      {items.map((item, i) => <li key={item.id} data-index={i} className="flex flex-col space-y-2">
          <Link to={item.href || "#"} className="text-base font-normal tracking-wide text-foreground hover:text-primary transition-colors">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="pl-4 border-l border-border space-y-2">
              <MobileMenuTree items={item.children} />
            </div>}
        </li>)}
    </ul>;
}

// Consumer for mobile navigation inside Sheet drawer
function MobileNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-4 py-4">
        <div className="h-5 w-24 bg-muted animate-pulse rounded-xs" />
        <div className="h-5 w-32 bg-muted animate-pulse rounded-xs" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-xs" />
        <div className="h-5 w-28 bg-muted animate-pulse rounded-xs" />
      </div>;
  }
  return <div className="py-4">
      <MobileMenuTree items={menuItems} />
    </div>;
}

// Product search results consumer inside popover
function SearchResultsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <p className="px-2 py-3 text-xs text-muted-foreground">Searching catalog...</p>;
  }
  if (error) {
    return <p className="px-2 py-3 text-xs text-destructive">Failed to load search results.</p>;
  }
  if (!products.length) {
    return <p className="px-2 py-3 text-xs text-muted-foreground">No garments found matching query.</p>;
  }
  const previewProducts = products.slice(0, 6);
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-2">
      {previewProducts.map((product, i) => <Link key={product.id} data-index={i} to={product.link || "#"} className="flex items-center gap-3 border border-border bg-card p-2 hover:bg-muted/50 transition-colors">
          {product.featured_image_url && <div className="h-12 w-10 shrink-0 overflow-hidden bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" loading="lazy" />
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-foreground">{product.title}</p>
            {(product.formatted_discounted_price || product.formatted_price) && <p className="text-xs text-primary font-medium mt-0.5">
                {product.formatted_discounted_price ?? product.formatted_price}
              </p>}
          </div>
        </Link>)}
    </div>;
}

// Search Popover Trigger & Container
function SearchHeaderControl() {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <EcommerceProductsProvider wp_query={wp_query}>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="hover:bg-transparent text-foreground hover:text-primary transition-colors" aria-label="Search collection">
            <Search className="size-4" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 sm:w-96 bg-background border border-border p-4 shadow-md space-y-3">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Search</p>
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search dresses, tops, outerwear..." className="w-full" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {!hasSearchQuery ? <p className="py-2 text-xs text-muted-foreground">Type keywords to search available garments.</p> : <div className="space-y-2">
                <SearchResultsConsumer />
                <Link to={buildProductSearchUrl(searchValue)} className="block text-xs font-medium text-primary hover:underline pt-1 border-t border-border">
                  View all results
                </Link>
              </div>}
          </div>
        </PopoverContent>
      </Popover>
    </EcommerceProductsProvider>;
}
export default function Header() {
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const accountUrl = useWooAccountUrl();

  // Scroll detection for header elevation
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll handler for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className="sticky top-0 z-40 w-full transition-colors duration-200">
      {/* Top Announcement Bar */}
      {showAnnouncement && <div className="w-full bg-secondary text-secondary-foreground text-xs py-2 px-4 transition-all">
          <div className="max-w-[1360px] mx-auto flex items-center justify-between">
            <div className="flex-1 text-center font-normal tracking-wide">
              <span>Complimentary shipping on all orders over $100</span>
            </div>
            <Button variant="ghost" size="icon" className="h-5 w-5 p-0 text-secondary-foreground hover:text-secondary-foreground/70 hover:bg-transparent" onClick={() => setShowAnnouncement(false)} aria-label="Dismiss banner">
              <X className="size-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>}

      {/* Main Navigation Header */}
      <header className={`w-full bg-background transition-shadow duration-200 ${isScrolled ? "border-b border-border shadow-xs" : "border-b border-border/40"}`}>
        <div className="max-w-[1360px] mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-4">
          {/* Left Zone: Mobile Hamburger & Desktop Navigation */}
          <div className="flex items-center gap-3">
            {/* Mobile Sheet Drawer Trigger */}
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-transparent text-foreground hover:text-primary" aria-label="Open menu">
                    <Menu className="size-5" aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] sm:w-[360px] p-6 flex flex-col justify-between">
                  <div className="space-y-6">
                    <SheetHeader className="p-0 text-left">
                      <SheetTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        Navigation
                      </SheetTitle>
                    </SheetHeader>

                    {/* Mobile Menu Consumer */}
                    <MenuProvider menu_id="22" className="w-full">
                      <MobileNavConsumer />
                    </MenuProvider>
                  </div>

                  <div className="border-t border-border pt-6 space-y-3">
                    <Link to={accountUrl} className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors">
                      <User className="size-4" aria-hidden="true" />
                      <span>My Account</span>
                    </Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop Navigation */}
            <MenuProvider menu_id="22" className="hidden lg:block">
              <DesktopNavConsumer />
            </MenuProvider>
          </div>

          {/* Center Zone: Brand Logo */}
          <div className="flex items-center justify-center">
            <Link to="/" className="flex items-center" aria-label="yishengfushi home">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          {/* Right Zone: Utility Actions (Search, Account, Cart) */}
          <div className="flex items-center justify-end gap-1 sm:gap-2">
            {/* Search popover */}
            <SearchHeaderControl />

            {/* Account Icon Link */}
            <Button asChild variant="ghost" size="icon" className="hover:bg-transparent text-foreground hover:text-primary transition-colors hidden sm:inline-flex">
              <Link to={accountUrl} aria-label="My Account">
                <User className="size-4" aria-hidden="true" />
              </Link>
            </Button>

            {/* Cart Trigger with dynamic count */}
            <CartIcon iconType="bag" cartLayout="drawer" variant="ghost" size="icon" iconSize="size-4" className="hover:bg-transparent text-foreground hover:text-primary transition-colors" />
          </div>
        </div>
      </header>
    </section>;
}